/**
 * AI CONTEXT
 * Purpose: shared controlled-sort state for data tables rendered via Table2.
 * Handles: reading/persisting per-table sort field + order in the userSettings
 *   `tableSorts` map, and sorting the full row set before truncation. Clearing
 *   a column sort falls back to the table's natural (base) order, which the
 *   caller controls by ordering rows before calling sortRows.
 * Used by: the table data hooks (and inline table views without a hook).
 */
import { useCallback, useState } from "react"

import { USER_SETTINGS } from "#constants/globalConstants"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  type TableSortSetting,
  type UserSettings,
} from "#customTypes/globalTypes"
import { type TableData } from "#customTypes/types"

export type SortOrderValue = "ascend" | "descend"

// Sentinel field used when the user clears a column sort. It does not match any
// column key, so no header arrow shows and rows keep their base order.
export const CLEARED_SORT_FIELD = "lastUpdatedAt"
const CLEARED_SORT_ORDER: SortOrderValue = "descend"

type TableSortsMap = NonNullable<UserSettings["tableSorts"]>

interface UseTableSortParams<T extends TableData> {
  // SETTINGS_KEYS value identifying the table (e.g. "tryouts").
  tableKey: string
  // Column keys allowed to drive sorting. Must be a stable reference.
  sortableFields: readonly string[]
  // Initial sort before the user picks one. If not in sortableFields the table
  // simply shows its base order.
  defaultField?: string
  defaultOrder?: SortOrderValue
  // Optional value resolver for columns whose display string is not sortable
  // (e.g. a formatted date backed by an epoch number). Must be stable.
  getSortValue?: (row: T, field: string) => string | number
}

export const useTableSort = <T extends TableData>({
  tableKey,
  sortableFields,
  defaultField = CLEARED_SORT_FIELD,
  defaultOrder = CLEARED_SORT_ORDER,
  getSortValue,
}: UseTableSortParams<T>) => {
  const { getUserSetting, setUserSetting } = useUserContext()

  // Local override applied immediately on header click. setUserSetting writes to
  // a ref + debounced Firebase flush, so the persisted value only re-renders the
  // table after the write echoes back (~500ms). Holding the active sort in state
  // makes the reorder instant; persistence still happens in the background.
  const [override, setOverride] = useState<TableSortSetting | null>(null)

  const tableSorts =
    getUserSetting<TableSortsMap>(USER_SETTINGS.TABLE_SORTS) ?? {}
  const persisted = tableSorts[tableKey]
  // Persisted value is the initial/fallback so a saved sort restores on load
  // (including after currentUser loads async); the local override wins once the
  // user interacts.
  const resolvedField = override?.field ?? persisted?.field ?? defaultField
  const resolvedOrder = override?.order ?? persisted?.order ?? defaultOrder

  // A persisted user setting may reference a field that's since been removed
  // from sortableFields (saved sorts outlive releases). sortRows skips it, so
  // report the inert sentinel here too — otherwise Table2 could paint a sort
  // arrow on a column whose rows are actually in base order.
  const isSortable = sortableFields.includes(resolvedField)
  const sortField = isSortable ? resolvedField : CLEARED_SORT_FIELD
  const sortOrder = isSortable ? resolvedOrder : CLEARED_SORT_ORDER

  const sortRows = useCallback(
    (rows: T[]): T[] => {
      if (!sortableFields.includes(sortField)) return rows

      const direction = sortOrder === "descend" ? -1 : 1
      return [...rows].sort((a, b) => {
        const aValue = getSortValue ? getSortValue(a, sortField) : a[sortField]
        const bValue = getSortValue ? getSortValue(b, sortField) : b[sortField]

        if (typeof aValue === "number" && typeof bValue === "number") {
          return (aValue - bValue) * direction
        }
        return (
          String(aValue ?? "").localeCompare(String(bValue ?? "")) * direction
        )
      })
    },
    [sortField, sortOrder, sortableFields, getSortValue]
  )

  const onSortChange = useCallback(
    (field: string, order: SortOrderValue | null) => {
      // Clearing a column sort falls back to the natural/base order.
      const next: TableSortSetting = order
        ? { field, order }
        : { field: CLEARED_SORT_FIELD, order: CLEARED_SORT_ORDER }

      // Apply instantly via local state, then persist in the background.
      setOverride(next)

      const existing =
        getUserSetting<TableSortsMap>(USER_SETTINGS.TABLE_SORTS) ?? {}
      setUserSetting(USER_SETTINGS.TABLE_SORTS, {
        ...existing,
        [tableKey]: next,
      })
    },
    [getUserSetting, setUserSetting, tableKey]
  )

  return { sortField, sortOrder, sortRows, onSortChange }
}
